<!-- Image field of a Vue dialog (video, source and analysed video thumbnails, creator and profile photos):
     the box shows the image as its background. With the "Or paste an image URL" field (urlField) a click opens the file
     chooser; without it a click opens the box menu under the box (#<id>Menu): "Upload from computer" (the file chooser)
     or "From a link" (an address field with a preview; "Use this image" copies the image like an uploaded file when the
     site lets the app read it, otherwise keeps the address: imageFromLink in src/features/images.ts), plus "Remove photo"
     with menuRemove. The image file is shrunk (readImg, longest
     side `max` pixels) and stored apart (mediaPut → "media:<id>"; keep="data" keeps the small data URL instead, for the
     profile list). An image file dropped on the box does the same; an image dragged from a web page keeps its address,
     like the optional "Or paste an image URL" field (urlField: only http(s) image addresses count, the image stays a link;
     emptying the field removes an image taken from a URL). The trash button inside the box removes the image. The box is a button for the keyboard too (Tab, then Enter or
     Space opens the file chooser).
     Same markup as the legacy boxes it replaced (#thumbnailBox, #srcThumbBox, #avThumbBox, #creatorAvatarBox…; the legacy
     DROP_BOXES). Logic: src/features/images.ts.
     v-model: the stored value ('' | "media:<id>" | http(s) address | data URL). It renders the box and, with urlField,
     the URL field as two siblings (no wrapper): put them in the dialog's own wrapper. Placeholder: `placeholder` (camera
     icon + text) or the default slot. fallback: an image shown while there is none (e.g. the YouTube thumbnail of the
     link), without the trash button. preview=false: the box is only its slot (e.g. a camera button), the image is shown
     elsewhere. clear() through a template ref, for a remove button drawn outside the box. -->
<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from '../../composables/useI18n'
import { imageDrop, imageFromLink, imageUrlText, typedImageUrl } from '../../features/images'
import { isWebImg } from '../../utils/url'
import { uiAlert } from '../../stores/notices'
import { safeUrl } from '../../utils/css'
import { mediaPut } from '../../features/cloud/media'
import { readImg } from '../../utils/image'

const props = withDefaults(defineProps<{
  modelValue: string
  /** id of the box (keep the legacy one: drop targets, tests) */
  id?: string
  /** id of the hidden file input */
  fileId?: string
  /** Classes of the box: shape and size, written whole (default: the source thumbnail, a 9rem-high rounded rectangle) */
  boxClass?: string
  /** More inline style of the box, after the image (e.g. 'aspect-ratio: 9 / 16;' for the vertical video cover) */
  boxStyle?: string
  /** Longest side of the stored image, in pixels (800 sources and analysed videos, 1280 video thumbnails, 400 creator
   *  photos, 256 profile photos) */
  max?: number
  /** 'media': stored apart as "media:<id>" (default); 'data': kept as a data URL in the value */
  keep?: 'media' | 'data'
  /** Text of the default placeholder (camera icon + text) */
  placeholder?: string
  placeholderClass?: string
  /** Trash button inside the box when there is an image */
  remove?: boolean
  removeId?: string
  removeClass?: string
  /** Image shown while there is none (not stored) */
  fallback?: string
  /** "Or paste an image URL" field under the box; it also lets the box take images dragged from a web page */
  urlField?: boolean
  urlId?: string
  urlPlaceholder?: string
  urlClass?: string
  /** "Remove photo" in the box menu (boxes without a remove button of their own) */
  menuRemove?: boolean
  /** Text of the menu's remove entry (default "Remove photo") */
  removeLabel?: string
  /** false: the box never shows the image, only its slot (a small button that changes an image shown elsewhere, e.g.
   *  the creator banner in the creator's profile) */
  preview?: boolean
  /** Classes of the box menu, written whole (default: under the box, as wide as it, at most 20rem) */
  menuClass?: string
}>(), {
  id: undefined,
  fileId: undefined,
  boxClass: 'bg-thumbBg border border-red-950/40 rounded-xl h-36 flex items-center justify-center cursor-pointer relative overflow-hidden bg-cover bg-center hover:border-brand/60 transition',
  boxStyle: '',
  max: 800,
  keep: 'media',
  placeholder: '',
  placeholderClass: 'text-brand text-xs font-medium',
  remove: true,
  removeId: undefined,
  removeClass: 'absolute top-2 right-2 bg-black/70 text-white p-2 rounded-full text-xs',
  fallback: '',
  urlField: false,
  urlId: undefined,
  urlPlaceholder: '',
  menuRemove: false,
  removeLabel: '',
  preview: true,
  menuClass: 'mt-2 w-full max-w-xs',
  urlClass: 'mt-2 w-full bg-darkinput border border-gray-800 rounded-lg px-3 py-2 text-xs text-gray-100 focus:outline-none focus:border-brand'
})
const emit = defineEmits<{ 'update:modelValue': [value: string] }>()
const { t } = useI18n()

const fileInput = ref<HTMLInputElement | null>(null)
const shown = computed(() => (props.preview ? props.modelValue || props.fallback : ''))
// A string style (like the legacy boxes): mediaPaint (src/features/cloud/media.ts) fills in an image that arrives later from Google Drive,
// and Vue leaves the style alone until the value changes
const style = computed(() => `background-image: ${shown.value ? `url("${safeUrl(shown.value)}")` : 'none'};${props.boxStyle ? ' ' + props.boxStyle : ''}`)

function set(v: string) { if (v !== props.modelValue) emit('update:modelValue', v) }

// ===== URL field =====
const urlText = ref(imageUrlText(props.modelValue))
// The field shows the address only when the image comes from a URL (what is being typed stays while it means the same)
watch(() => props.modelValue, v => { if (urlText.value.trim() !== imageUrlText(v)) urlText.value = imageUrlText(v) })
function typed(e: Event) {
  urlText.value = (e.target as HTMLInputElement).value
  const v = typedImageUrl(props.modelValue, urlText.value)
  if (v !== null) { reading++; set(v) }
}

// ===== File =====
let reading = 0   // the latest file read wins (a removal or another file in the meantime cancels it)
/** A file → the stored value (shrunk; stored apart, or a data URL with keep="data") */
async function store(f: Blob): Promise<string> {
  const img = await readImg(f, props.max)
  return props.keep === 'data' ? img : mediaPut(img, 'img')
}
async function useFile(f: File) {
  const n = ++reading
  const v = await store(f)
  if (n === reading) set(v)
}
function picked(e: Event) {
  const inp = e.target as HTMLInputElement, f = inp.files && inp.files[0]
  if (f) void useFile(f)
  inp.value = ''   // the same file can be chosen again
}
function chooseFile() { menu.value = ''; fileInput.value?.click() }
function clear() { reading++; menu.value = ''; set(''); if (fileInput.value) fileInput.value.value = '' }

// ===== Box menu (boxes without the URL field): upload from the computer or take the image from a link =====
const hasMenu = computed(() => !props.urlField)
const menuId = computed(() => (props.id ? props.id + 'Menu' : undefined))
const menu = ref<'' | 'menu' | 'link'>('')
const link = ref(''), linkBad = ref(false), linkBusy = ref(false)
const linkInput = ref<HTMLInputElement | null>(null)
const linkOk = computed(() => isWebImg(link.value))
watch(link, () => { linkBad.value = false })
function open() {
  if (!hasMenu.value) { fileInput.value?.click(); return }
  menu.value = menu.value ? '' : 'menu'
}
function askLink() {
  menu.value = 'link'; link.value = ''; linkBad.value = false
  void nextTick(() => linkInput.value?.focus())
}
async function useLink() {
  if (!linkOk.value || linkBad.value || linkBusy.value) return
  const n = ++reading
  linkBusy.value = true
  try {
    const v = await imageFromLink(link.value, store)
    if (n === reading && v) { set(v); menu.value = '' }
  } finally { linkBusy.value = false }
}
async function linkDropped(url: string) {
  const n = ++reading
  const v = await imageFromLink(url, store)
  if (n === reading && v) set(v)
}

// ===== Drag and drop =====
const over = ref(false)
function dragOver(e: DragEvent) { if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'; over.value = true }
function dragLeave(e: DragEvent) { if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) over.value = false }
function drop(e: DragEvent) {
  over.value = false
  const dt = e.dataTransfer
  if (!dt) return
  const files = [...(dt.files || [])]
  const r = imageDrop(files, dt.getData('text/uri-list') || dt.getData('text/plain') || '', true)
  if (r.kind === 'file') void useFile(files[r.index])
  else if (r.kind === 'url') {
    // Box with the URL field: the address stays a link (it shows in the field); otherwise a copy when the site allows it
    if (props.urlField) { reading++; set(r.url) } else void linkDropped(r.url)
  }
  else if (r.kind === 'alert') {
    uiAlert(r.alert === 'notImage' ? t('You can only drop an image here (JPG, PNG, WebP…).')
      : r.alert === 'saveFirst' ? t("I can't use this image: save it on your computer and drop the file.")
        : t('You can drop an image file from your computer here.'))
  }
}

defineExpose({ clear, open })
</script>

<template>
  <div :id="id" :class="[boxClass, 'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand', { 'drag-over': over }]" :style="style" role="button" tabindex="0"
    :aria-label="placeholder || t('Choose an image')" @click="open" @keydown.self.enter.prevent="open" @keydown.self.space.prevent="open"
    @dragover.prevent="dragOver" @dragleave="dragLeave" @drop.prevent.stop="drop">
    <input :id="fileId" ref="fileInput" type="file" accept="image/*" class="hidden" @click.stop @change="picked">
    <template v-if="!shown">
      <slot><span :class="placeholderClass"><i class="fas fa-camera mr-1"></i><span>{{ placeholder }}</span></span></slot>
    </template>
    <button v-if="remove" :id="removeId" type="button" :class="[removeClass, { hidden: !modelValue }]" @click.stop="clear"><i class="fas fa-trash"></i></button>
  </div>
  <div v-if="hasMenu && menu" :id="menuId" :class="[menuClass, 'bg-darkinput border border-gray-800 rounded-xl p-2 text-xs']" @click.stop>
    <div v-if="menu === 'menu'" class="flex flex-col gap-1">
      <button type="button" data-img="file" class="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-left font-bold text-gray-300 hover:text-gray-100 hover:bg-darkbtn" @click="chooseFile"><i class="fas fa-upload w-4 text-brand"></i>{{ t('Upload from computer') }}</button>
      <button type="button" data-img="link" class="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-left font-bold text-gray-300 hover:text-gray-100 hover:bg-darkbtn" @click="askLink"><i class="fas fa-link w-4 text-brand"></i>{{ t('From a link') }}</button>
      <button v-if="menuRemove && modelValue" type="button" data-img="remove" class="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-left font-bold text-gray-400 hover:text-brand hover:bg-darkbtn" @click="clear"><i class="fas fa-trash w-4"></i>{{ removeLabel || t('Remove photo') }}</button>
    </div>
    <div v-else class="space-y-2">
      <input ref="linkInput" v-model="link" type="url" inputmode="url" autocapitalize="off" autocorrect="off" spellcheck="false" autocomplete="off"
        :placeholder="t('Paste the image address (https://…)')" class="w-full bg-darkcard border border-gray-800 rounded-lg px-3 py-2 text-xs text-gray-100 focus:outline-none focus:border-brand"
        @keydown.enter.prevent="useLink">
      <div v-if="linkOk" class="flex items-center gap-2">
        <img v-show="!linkBad" :src="link.trim()" alt="" referrerpolicy="no-referrer" class="w-12 h-12 rounded-lg object-cover bg-thumbBg flex-shrink-0" @error="linkBad = true">
        <p v-if="linkBad" class="text-[11px] text-brand font-semibold">{{ t('This link doesn\'t open an image. On the web page, right-click the image and choose "Copy image address".') }}</p>
      </div>
      <p v-else-if="link.trim()" class="text-[11px] text-gray-500">{{ t('Paste the address of an image, starting with https://') }}</p>
      <div class="flex justify-end gap-2">
        <button type="button" class="px-3 py-1.5 rounded-lg bg-darkbtn text-gray-300 hover:text-gray-100 font-bold" @click="menu = 'menu'">{{ t('Back') }}</button>
        <button type="button" data-img="use" :disabled="!linkOk || linkBad || linkBusy" class="px-3 py-1.5 rounded-lg bg-brand hover:bg-brandDark text-white font-bold disabled:opacity-40" @click="useLink">
          <i v-if="linkBusy" class="fas fa-spinner fa-spin mr-1"></i>{{ t('Use this image') }}</button>
      </div>
    </div>
  </div>
  <input v-if="urlField" :id="urlId" :value="urlText" type="url" inputmode="url" autocapitalize="off" autocorrect="off" spellcheck="false" autocomplete="off" :placeholder="urlPlaceholder || t('Or paste an image URL')" :class="urlClass" @input="typed">
</template>
